import { Button, Form, Input, Modal, Space, message } from "antd";
import { useState } from "react";
import { transportApi } from "../../api/transportApi";
import { getApiErrorMessage } from "../../utils/apiError";
import type { LookupItem } from "./LookupModal";

interface BranchCreateModalProps {
  open: boolean;
  /** Pre-fill branch name from the search text that triggered "Add". */
  prefillName?: string;
  onClose: () => void;
  /** Called with the newly created item so the parent can seed + select it. */
  onCreated: (item: LookupItem) => void;
}

export const BranchCreateModal = ({
  open,
  prefillName,
  onClose,
  onCreated,
}: BranchCreateModalProps) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Prefill when the modal opens
  const handleAfterOpen = (visible: boolean) => {
    if (visible) {
      form.resetFields();
      if (prefillName) form.setFieldValue("branchName", prefillName);
    }
  };

  const handleSave = async () => {
    let values: any;
    try { values = await form.validateFields(); }
    catch { return; }

    setSaving(true);
    try {
      const created = await transportApi.createBranchLookup({
        branchName: values.branchName,
        branchCode: values.branchCode ?? undefined,
        city: values.city ?? undefined,
        state: values.state ?? undefined,
      });
      message.success(`Branch "${created.label}" created`);
      onCreated(created);
      form.resetFields();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to create branch"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Add New Branch / Station"
      open={open}
      onCancel={onClose}
      afterOpenChange={handleAfterOpen}
      destroyOnClose
      width={480}
      footer={
        <Space style={{ justifyContent: "flex-end", width: "100%" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={() => void handleSave()}>
            Save Branch
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
        <Form.Item
          name="branchName"
          label="Branch / Station Name"
          rules={[{ required: true, message: "Enter branch name" }]}
        >
          <Input placeholder="e.g. Delhi Main Branch" autoFocus />
        </Form.Item>
        <Form.Item name="branchCode" label="Branch Code">
          <Input placeholder="e.g. DEL01" style={{ textTransform: "uppercase" }}
            onChange={e => form.setFieldValue("branchCode", e.target.value.toUpperCase())} />
        </Form.Item>
        <Form.Item name="city" label="City">
          <Input placeholder="e.g. Delhi" />
        </Form.Item>
        <Form.Item name="state" label="State">
          <Input placeholder="e.g. Delhi" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
